import React from "react"
import { Button } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowDown,
  faArrowUp,
  faPen,
  faXmark,
} from "@fortawesome/free-solid-svg-icons"
import { StagedBlock, TemplateCatalogueItem } from "types/template.types"
import { firstNameValue } from "./templateInputs"
import { TrayCheck } from "./useStagingTray"

interface StagingTrayProps {
  blocks: StagedBlock[]
  /** The catalogue, to know which input of a block is its NAME */
  items: TemplateCatalogueItem[]
  check: TrayCheck
  /** True while Insert is under way */
  inserting?: boolean
  onEdit: (block: StagedBlock) => void
  onRemove: (id: string) => void
  onMove: (from: number, to: number) => void
  onInsert: () => void
}

/** "Insert 1 block" / "Insert 3 blocks". */
const insertLabel = (count: number): string =>
  `Insert ${count} ${count === 1 ? "block" : "blocks"}`

/**
 * The staging tray at the bottom of the Templates dialog: one row per staged
 * block (its template and first question name) with edit, remove and move
 * up/down; the check's errors under the block that causes them; and the
 * Insert button, which waits for a clean check.
 *
 * @param props - The component props
 * @returns The tray
 */
const StagingTray: React.FC<StagingTrayProps> = ({
  blocks,
  items,
  check,
  inserting = false,
  onEdit,
  onRemove,
  onMove,
  onInsert,
}) => {
  const blocked = check.state !== "ok" || inserting

  return (
    <div className="tpl-tray" data-testid="staging-tray">
      <div className="tpl-tray-head">
        <span className="tpl-tray-title">Staged</span>
        <span className="tpl-tray-status" aria-live="polite">
          {check.state === "checking" && "Checking"}
          {check.state === "errors" && "Fix the errors before inserting"}
        </span>
        <Button
          type="primary"
          className="ml-auto"
          disabled={blocked}
          onClick={onInsert}
        >
          {insertLabel(blocks.length)}
        </Button>
      </div>
      <ol className="tpl-tray-list">
        {blocks.map((block, i) => {
          const name = firstNameValue(block, items)
          const errors = check.byBlock[i] ?? []
          return (
            <li
              key={block.id}
              className="tpl-tray-row"
              data-testid="tray-row"
              aria-invalid={errors.length > 0}
            >
              <div className="tpl-tray-line">
                <span className="tpl-tray-name">{block.title}</span>
                {name && <span className="tpl-tray-q">{name}</span>}
                <span className="tpl-tray-actions">
                  <Button
                    type="text"
                    aria-label={`Move ${block.title} up`}
                    icon={<FontAwesomeIcon icon={faArrowUp} />}
                    disabled={i === 0}
                    onClick={() => onMove(i, i - 1)}
                  />
                  <Button
                    type="text"
                    aria-label={`Move ${block.title} down`}
                    icon={<FontAwesomeIcon icon={faArrowDown} />}
                    disabled={i === blocks.length - 1}
                    onClick={() => onMove(i, i + 1)}
                  />
                  <Button
                    type="text"
                    aria-label={`Edit ${block.title}`}
                    icon={<FontAwesomeIcon icon={faPen} />}
                    onClick={() => onEdit(block)}
                  />
                  <Button
                    type="text"
                    aria-label={`Remove ${block.title}`}
                    icon={<FontAwesomeIcon icon={faXmark} />}
                    onClick={() => onRemove(block.id)}
                  />
                </span>
              </div>
              {errors.length > 0 && (
                <ul className="tpl-tray-errors">
                  {errors.map((message, j) => (
                    <li key={j}>{message}</li>
                  ))}
                </ul>
              )}
            </li>
          )
        })}
      </ol>
      {check.general.length > 0 && (
        <ul className="tpl-tray-errors" data-testid="tray-general-errors">
          {check.general.map((message, j) => (
            <li key={j}>{message}</li>
          ))}
        </ul>
      )}
      {check.warnings.length > 0 && (
        <ul className="tpl-tray-warnings" data-testid="tray-warnings">
          {check.warnings.map((message, j) => (
            <li key={j}>{message}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default StagingTray
